import { named, withDependencies } from '@wix/thunderbolt-ioc'
import type { IAnimations, IEditorAnimations, SequenceItemData, AnimationsPageState, ScrollManager } from '../types'
import type { Callback } from 'thunderbolt-viewer-manager-types'
import { createAnimatorManager } from '../animatorManagerFactory'
import type { ViewMode } from '@wix/thunderbolt-symbols'
import { FeatureStateSymbol, LayoutDoneServiceSymbol, ILayoutDoneService } from '@wix/thunderbolt-symbols'
import type { IFeatureState } from 'thunderbolt-feature-state'
import { name } from '../symbols'
import { TriggerVariant, ScrubAnimationDef } from '@wix/thunderbolt-becky-types'

const CLEAR_PROPS = 'clip,clipPath,webkitClipPath,willChange,opacity,transform,transformOrigin'
const clearParams = { props: CLEAR_PROPS, immediateRender: false }

const editorAnimationsFactory = (
	featureState: IFeatureState<AnimationsPageState>,
	layoutDoneService: ILayoutDoneService
): IEditorAnimations => {
	let currentViewMode = 'desktop' as ViewMode
	let _animator = createAnimatorManager(currentViewMode)
	const effectsAnimator = createAnimatorManager('motion')
	let nextSequenceId = 0
	const activePreviewAnimation: { [key: string]: any } = {}
	let scrollManagers: Array<ScrollManager> = []
	const scrubTargetIds: Set<string> = new Set()

	featureState.update(() => ({
		managers: Promise.resolve({
			animatorManager: _animator,
			effectManager: effectsAnimator,
		}),
	}))

	const runPreviewAnimationWithClear = (
		sequenceData: SequenceItemData,
		idsToClear: Array<string>,
		onComplete: Callback,
		isMotion: boolean,
		repeats: number = 0
	) => {
		const sequenceId = `seq_${nextSequenceId++}`
		const baseClearData = {
			name: 'BaseClear',
			targetId: idsToClear,
			duration: 0,
			delay: 0,
			params: clearParams,
		}
		const manager = isMotion ? effectsAnimator : _animator

		activePreviewAnimation[sequenceId] = {
			manager,
			sequence: manager.runSequence([sequenceData, { type: 'Animation', data: baseClearData }], {
				repeats,
				callbacks: {
					onComplete: () => {
						delete activePreviewAnimation[sequenceId]
						onComplete()
					},
				},
			}),
		}

		return sequenceId
	}

	const startScrubPreview = (
		animations: Record<string, ScrubAnimationDef>,
		triggers: Record<string, TriggerVariant>
	) => {
		const scrubScenes: Record<string, () => ScrollScene> = effectsAnimator.createScrubAnimations(animations)
		scrollManagers = effectsAnimator.startScrubAnimations(triggers, scrubScenes)
		Object.values(animations).forEach((animation) => scrubTargetIds.add(animation.targetId))
	}

	const updateScrubAnimationsPreview = (
		animations: Record<string, ScrubAnimationDef>,
		triggers: Record<string, TriggerVariant>
	) => {
		stopScrubPreview()
		requestAnimationFrame(() => startScrubPreview(animations, triggers))
	}

	const stopScrubPreview = () => {
		effectsAnimator.killPreviewScrubAnimations(scrollManagers)
		effectsAnimator.clearPreviewScrubAnimations(scrubTargetIds)
		scrubTargetIds.clear()
	}

	const runPreviewAnimation: IEditorAnimations['runPreviewAnimation'] = (
		targetId,
		animationDef,
		onComplete,
		isMotion = false
	) => {
		const { duration, delay, easing, iterations, params: animationParams } = animationDef
		const repeats = iterations || iterations === 0 ? iterations - 1 : 0
		let params = animationParams

		if (isMotion) {
			params = { ...params, easing }
		}

		const animationData = { name: animationDef.name, targetId, duration, delay, params }

		return runPreviewAnimationWithClear(
			{ type: 'Animation', data: animationData },
			[targetId],
			onComplete,
			isMotion,
			repeats
		)
	}

	const runPreviewTransition: IEditorAnimations['runPreviewTransition'] = (
		srcIds,
		targetIds,
		animationDef,
		onComplete
	) => {
		const { duration, delay, params } = animationDef
		const transitionData = { name: animationDef.name, srcId: srcIds, targetId: targetIds, duration, delay, params }

		return runPreviewAnimationWithClear(
			{ type: 'Transition', data: transitionData },
			[...srcIds, ...targetIds],
			onComplete,
			false
		)
	}

	const stopPreviewAnimation: IEditorAnimations['stopPreviewAnimation'] = (animationId) => {
		const animation = activePreviewAnimation[animationId]

		if (animation?.sequence) {
			delete activePreviewAnimation[animationId]
			animation.manager.kill(animation.sequence.get(), 1)
		}
	}

	const stopAllPreviewAnimations: IEditorAnimations['stopAllPreviewAnimations'] = () => {
		Object.keys(activePreviewAnimation).forEach((animationId) => stopPreviewAnimation(animationId))
	}

	return {
		runPreviewAnimation,
		runPreviewTransition,
		stopPreviewAnimation,
		stopAllPreviewAnimations,
		updateScrubAnimationsPreview,
		startScrubPreview,
		stopScrubPreview,
		getPreviewAnimationsService: () => {
			return {
				sequence: (params: { [key: string]: any }) => {
					params = params || {}

					if (!params.callbacks) {
						params.callbacks = {}
					}

					const { onStart, onComplete } = params.callbacks

					if (onStart) {
						params.callbacks.onStart = (instance: any) => {
							onStart(instance)
							layoutDoneService.enableLayoutDone(false)
						}
					} else {
						params.callbacks.onStart = () => layoutDoneService.enableLayoutDone(false)
					}

					if (onComplete) {
						params.callbacks.onComplete = (instance: any) => {
							onComplete(instance)
							layoutDoneService.enableLayoutDone(true)
						}
					} else {
						params.callbacks.onComplete = () => layoutDoneService.enableLayoutDone(true)
					}

					return _animator.createSequence(params)
				},
				animate: _animator.createAnimationFromParams,
				updateViewMode: (viewMode: ViewMode) => {
					currentViewMode = viewMode.toLowerCase() as ViewMode
					_animator = createAnimatorManager(currentViewMode)
				},
			}
		},
	}
}

const editorAnimationsFactoryFactory = (featureState: IFeatureState<AnimationsPageState>): IAnimations => {
	return {
		getInstance() {
			return featureState.get().managers.then(({ animatorManager }) => animatorManager)
		},
		getEffectsInstance() {
			return featureState.get().managers.then(({ effectManager }) => effectManager)
		},
	}
}

export const EditorAnimations = withDependencies(
	[named(FeatureStateSymbol, name), LayoutDoneServiceSymbol],
	editorAnimationsFactory
)
export const EditorAnimationsFactory = withDependencies(
	[named(FeatureStateSymbol, name)],
	editorAnimationsFactoryFactory
)
